import { useCallback, useEffect, useMemo, useState } from 'react';

import { ImageModalV2Mode } from '../hooks/useImageModalV2Mode';
import { getImageModalV2BottomSurface, IMAGE_MODAL_V2_BOTTOM_SURFACE } from './imageModalV2BottomSurface';
import type { ImageModalV2ErrorToastState } from './imageModalV2ErrorToastTypes';
import {
    IMAGE_MODAL_V2_OPERATION_MESSAGE,
    type ImageModalV2OperationToastState,
} from './imageModalV2OperationToastTypes';
import { useImageModalV2BottomSurfacePresentation } from './useImageModalV2BottomSurfacePresentation';
import { useImageModalV2OperationToastPresentation } from './useImageModalV2OperationToastPresentation';
import { type ImageModalV2ModalToast, useImageModalV2PresentedModalToast } from './useImageModalV2PresentedModalToast';

type UseImageModalV2BottomSurfaceOptions = {
    elementId: string;
    mode: ImageModalV2Mode;
    isToolPanelOpen: boolean;
};

/**
 * Owns the image modal's bottom surface: which of the competing surfaces (primary/secondary toolbar,
 * file-transfer status, operation/error toast) is shown, the animated handoff between them, and the
 * operation/error toast desired-state the editor drives.
 *
 * Composes the focused presentation hooks in the order their data flow requires — the operation toast's
 * minimum-presentation time resolves first (it decides whether the surface is occupied), the shared
 * presentation machine then arbitrates it, and the presented-toast reconciliation runs last (it needs the
 * resulting surface openness) — so the shell only has to render the result.
 */
export const useImageModalV2BottomSurface = ({
    elementId,
    mode,
    isToolPanelOpen,
}: UseImageModalV2BottomSurfaceOptions) => {
    const [desiredOperationToast, setDesiredOperationToast] = useState<ImageModalV2OperationToastState | null>(null);
    const [desiredErrorToast, setDesiredErrorToast] = useState<ImageModalV2ErrorToastState | null>(null);

    const operationToast = useImageModalV2OperationToastPresentation(desiredOperationToast);
    const desiredModalToast = useMemo<ImageModalV2ModalToast | null>(() => {
        if (operationToast) return { type: 'operation', toast: operationToast };
        if (desiredErrorToast) return { type: 'error', toast: desiredErrorToast };
        return null;
    }, [desiredErrorToast, operationToast]);

    const hasFileTransfer = mode === ImageModalV2Mode.UPLOAD || mode === ImageModalV2Mode.DOWNLOAD;
    const desiredBottomSurface = getImageModalV2BottomSurface({
        hasModalOperation: !!desiredModalToast,
        hasFileTransfer,
        wantsSecondaryToolbar: isToolPanelOpen,
    });
    const { openSurface, completeExit, getSurfaceState } =
        useImageModalV2BottomSurfacePresentation(desiredBottomSurface);
    const fileTransferSurface = getSurfaceState(IMAGE_MODAL_V2_BOTTOM_SURFACE.FILE_TRANSFER_STATUS);
    const operationSurface = getSurfaceState(IMAGE_MODAL_V2_BOTTOM_SURFACE.MODAL_OPERATION_STATUS);

    const onFileTransferSurfaceExited = useCallback(
        () => completeExit(IMAGE_MODAL_V2_BOTTOM_SURFACE.FILE_TRANSFER_STATUS),
        [completeExit],
    );
    const onModalOperationSurfaceExited = useCallback(
        () => completeExit(IMAGE_MODAL_V2_BOTTOM_SURFACE.MODAL_OPERATION_STATUS),
        [completeExit],
    );
    const {
        presentedModalToast,
        isOpen: isModalToastOpen,
        handleExitComplete: handleModalToastExitComplete,
    } = useImageModalV2PresentedModalToast({
        desiredModalToast,
        isSurfaceOpen: operationSurface.isOpen,
        onSurfaceExited: onModalOperationSurfaceExited,
    });

    // Only the operation toast's "saving changes" milestone hands off to the toolbar returning below it;
    // every other operation/error toast exits upward. The toast component itself is message-agnostic.
    const isSavingChangesToast =
        presentedModalToast?.type === 'operation' &&
        presentedModalToast.toast.message === IMAGE_MODAL_V2_OPERATION_MESSAGE.SAVING_CHANGES;

    const handleOperationToastChange = useCallback((nextToast: ImageModalV2OperationToastState | null) => {
        setDesiredOperationToast((currentToast) => {
            if (currentToast?.message !== nextToast?.message) return nextToast;

            if (currentToast?.progress?.type !== nextToast?.progress?.type) return nextToast;

            const currentPercentage =
                currentToast?.progress?.type === 'determinate' ? currentToast.progress.percentageComplete : undefined;
            const nextPercentage =
                nextToast?.progress?.type === 'determinate' ? nextToast.progress.percentageComplete : undefined;
            return currentPercentage === nextPercentage ? currentToast : nextToast;
        });
    }, []);

    const handleErrorToastChange = useCallback((nextToast: ImageModalV2ErrorToastState | null) => {
        setDesiredErrorToast((currentToast) => {
            if (currentToast?.message !== nextToast?.message) return nextToast;

            return currentToast?.onDismiss === nextToast?.onDismiss ? currentToast : nextToast;
        });
    }, []);

    // Operation/error toasts belong to the crop/background editors, so clear them when leaving those
    // modes to stop a stale milestone lingering on the view.
    useEffect(() => {
        if (mode === ImageModalV2Mode.CROP || mode === ImageModalV2Mode.BACKGROUND) return;
        setDesiredOperationToast(null);
        setDesiredErrorToast(null);
    }, [elementId, mode]);

    return {
        openSurface,
        completeExit,
        fileTransferSurface,
        operationSurface,
        onFileTransferSurfaceExited,
        presentedModalToast,
        isModalToastOpen,
        isSavingChangesToast,
        handleModalToastExitComplete,
        handleOperationToastChange,
        handleErrorToastChange,
    };
};
